import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?url';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useVariant, type Variant } from '../../ui/useMediaQuery';
import { osmStyle, routeFeatures, routeLayers, type RouteGeometry } from './routeGeo';
import styles from './RouteMap.module.css';

// MapLibre derives its worker URL from import.meta.url, which Vite's dev pre-bundling and the production chunk both break.
maplibregl.setWorkerUrl(workerUrl);

export function addRoute(map: maplibregl.Map, geometry: RouteGeometry, variant: Variant): void {
  const { route, start } = routeFeatures(geometry);
  map.addSource('route', { type: 'geojson', data: route });
  map.addSource('start', { type: 'geojson', data: start });
  routeLayers(variant).forEach((layer) => map.addLayer(layer));
}

const FIT = { padding: 40, maxZoom: 16 } as const;

function updateRoute(map: maplibregl.Map, geometry: RouteGeometry): void {
  const { route, start } = routeFeatures(geometry);
  (map.getSource('route') as maplibregl.GeoJSONSource).setData(route);
  (map.getSource('start') as maplibregl.GeoJSONSource).setData(start);
  map.fitBounds(geometry.bounds, FIT);
}

export function RouteMap({ geometry }: { geometry: RouteGeometry }) {
  const variant = useVariant();
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const map = useRef<maplibregl.Map | null>(null);
  // The load handler draws the geometry that is current when the style finishes loading.
  const latest = useRef(geometry);
  useLayoutEffect(() => {
    latest.current = geometry;
  }, [geometry]);

  useEffect(() => {
    if (node === null) return undefined;
    const instance = new maplibregl.Map({
      container: node, style: osmStyle(), bounds: latest.current.bounds,
      fitBoundsOptions: FIT, attributionControl: { compact: true }, cooperativeGestures: variant === 'phone',
    });
    instance.on('load', () => {
      addRoute(instance, latest.current, variant);
      map.current = instance;
    });
    return () => {
      map.current = null;
      instance.remove();
    };
  }, [node, variant]);

  // HeroRow memoises geometry on the streams query (staleTime: Infinity), so a new identity is a new route.
  useEffect(() => {
    if (map.current !== null) updateRoute(map.current, geometry);
  }, [geometry]);

  return <div ref={setNode} className={styles.map} role="img" aria-label="Route map" />;
}
